import FormControl from '@mui/material/FormControl';
import type { AnyFieldApi, AnyFieldMetaBase } from '@tanstack/react-form';
import { sortBy } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import { FORM_MESSAGES } from '~/common/constants';
import { createRequiredValidator } from '~/common/form/utils';
import MultiSelect from '~/common/mui/Inputs/Selects/MultiSelect/MultiSelect';
import { useSelectedAlert } from '~/stores/navigationStore';

import { DROPDOWN_FIELD } from './constants';
import {
  type ClassifyFormData,
  buildFieldErrors,
  useStore,
  withForm,
} from './hooks/ClassifyFormHook';
import {
  useDocumentAssociationsQuery,
  useDocumentTypeAssociationsQuery,
} from './hooks/queries';
import {
  type ClassifyDocumentAssociation,
  type ClassifyDocumentTypeAndCategory,
  type ClassifyDropDownField,
  type SelectOption,
} from './types';

function checkSingleOptionEquality(
  option: SelectOption,
  value: SelectOption | SelectOption[],
): boolean {
  return (
    option?.value?.associationId ===
    (Array.isArray(value)
      ? value[0]?.value?.associationId
      : value?.value?.associationId)
  );
}

function getDropDownOptions(
  options: ClassifyDocumentAssociation[],
): SelectOption[] {
  return options?.map((option) => ({
    label: option.associationDetails,
    value: option,
  }));
}

function clearFieldErrors(field: AnyFieldApi): void {
  field.setMeta((prev: AnyFieldMetaBase) => ({ ...prev, errorMap: {} }));
}

function buildUnselectedFilter(
  selectedOptions: SelectOption[],
): (option: SelectOption) => boolean {
  return (option) =>
    !selectedOptions.some(
      (sel) =>
        sel?.value?.associationLdId === option?.value?.associationLdId &&
        sel?.value?.associationId === option?.value?.associationId,
    );
}

const ClassifyDynamicFormContent = withForm({
  defaultValues: {} as ClassifyFormData,
  render: function ClassifyDynamicFormContent({ form }) {
    const [currentDocumentTypeAndCategory, setCurrentDocumentTypeAndCategory] =
      useState<ClassifyDocumentTypeAndCategory | null>(null);

    const selectedDocument = useSelectedAlert();

    const { data: selectedDocumentAssociations } = useDocumentAssociationsQuery(
      selectedDocument.ruleReference.id,
      +selectedDocument?.additionalReferences.DocumentTypeId,
    );

    const { data: documentTypesAndCategories } =
      useDocumentTypeAssociationsQuery();

    // Watch documentType reactively to derive matching dropdown fields
    const currentDocumentType = useStore(
      form.store,
      (s) => s.values.documentType,
    );

    const handleChange = (
      isMulti: boolean,
      dropDownName: string,
      options: SelectOption[] | null,
      currentSelection: SelectOption | null = null,
    ) => {
      const safeOptions = Array.isArray(options) ? options : [];
      if (!isMulti) {
        const singleSelection = safeOptions[0] ?? null;
        form.setFieldValue(dropDownName, [singleSelection]);
        return;
      }
      // NA clears all other selections; any other selection removes NA
      if (currentSelection?.value?.associationId === 0) {
        form.setFieldValue(dropDownName, [currentSelection]);
      } else {
        const remaining = safeOptions.filter(
          (opt) => opt.value.associationId !== 0,
        );
        form.setFieldValue(dropDownName, remaining.length ? remaining : null);
      }
      if (safeOptions.length) {
        form.setFieldMeta(dropDownName, (prev: AnyFieldMetaBase) => ({
          ...prev,
          errorMap: {},
        }));
      }
    };

    const getDefaultValues = useCallback(
      (
        dropDownName: string,
        previouslySelectedOptions: ClassifyDocumentAssociation[],
        dropDownFields: ClassifyDropDownField[] | null | undefined,
      ): SelectOption[] => {
        const sortedPreviouslySelected = sortBy(
          previouslySelectedOptions,
          DROPDOWN_FIELD.associationDetails,
        );
        const allCurrentOptions: ClassifyDocumentAssociation[] = sortBy(
          dropDownFields?.find((f) => f.dropDownName === dropDownName)?.options,
          DROPDOWN_FIELD.associationDetails,
        );

        if (!allCurrentOptions?.length) return [];

        let selectedOptions: ClassifyDocumentAssociation[] = [];
        let selectedIdx = 0;
        let currentIdx = 0;

        while (
          selectedIdx < sortedPreviouslySelected?.length &&
          currentIdx < allCurrentOptions.length
        ) {
          const current = allCurrentOptions[currentIdx];
          const prev = sortedPreviouslySelected[selectedIdx];
          if (
            current.associationId === prev.associationId &&
            current.associationDetails === prev.associationDetails
          ) {
            selectedOptions = [...selectedOptions, current];
            selectedIdx += 1;
            currentIdx += 1;
          } else {
            currentIdx += 1;
          }
        }

        return getDropDownOptions(selectedOptions);
      },
      [],
    );

    // Update the active document type config when documentType or selectedDocument changes
    useEffect(() => {
      if (currentDocumentType?.value && selectedDocument) {
        const current = documentTypesAndCategories?.find(
          (dt: ClassifyDocumentTypeAndCategory) =>
            dt.documentTypeId === +currentDocumentType.value,
        );
        setCurrentDocumentTypeAndCategory(current);
        // Clear static field errors when dynamic dropdowns change
        form.setFieldMeta('documentCategory', (prev: AnyFieldMetaBase) => ({
          ...prev,
          errorMap: {},
        }));
        form.setFieldMeta('documentType', (prev: AnyFieldMetaBase) => ({
          ...prev,
          errorMap: {},
        }));
      } else {
        setCurrentDocumentTypeAndCategory(null);
      }
    }, [
      currentDocumentType,
      documentTypesAndCategories,
      selectedDocument,
      form,
    ]);

    // Restore previously saved associations when they load
    useEffect(() => {
      if (selectedDocumentAssociations) {
        for (const [dropDownName, previouslySelectedOptions] of Object.entries(
          selectedDocumentAssociations,
        )) {
          const values = getDefaultValues(
            dropDownName,
            previouslySelectedOptions as ClassifyDocumentAssociation[],
            currentDocumentTypeAndCategory?.dropDownFields,
          );
          form.setFieldValue(dropDownName, values?.length ? values : null);
        }
      }
    }, [
      selectedDocumentAssociations,
      currentDocumentTypeAndCategory?.dropDownFields,
      getDefaultValues,
      form,
    ]);

    // When documentType changes clear association values (or set NA defaults)
    useEffect(() => {
      if (
        currentDocumentType &&
        currentDocumentTypeAndCategory?.dropDownFields &&
        +selectedDocument.additionalReferences.DocumentTypeId !==
          currentDocumentType?.value
      ) {
        for (const dropDown of currentDocumentTypeAndCategory.dropDownFields) {
          if (dropDown.isDefaultNa) {
            const options = getDropDownOptions(
              dropDown.options.filter(
                (o: ClassifyDocumentAssociation) => o.associationId === 0,
              ),
            );
            form.setFieldValue(
              dropDown.dropDownName,
              options?.length ? options : null,
            );
          } else {
            form.setFieldValue(dropDown.dropDownName, null);
          }
        }
      }
    }, [
      selectedDocument,
      currentDocumentType,
      currentDocumentTypeAndCategory,
      form,
    ]);

    return (
      <>
        {currentDocumentTypeAndCategory?.dropDownFields?.map(
          (
            { dropDownName, options, isMulti }: ClassifyDropDownField,
            index: number,
          ) => {
            return (
              <form.Field
                key={dropDownName}
                name={dropDownName}
                validators={{
                  onSubmit: createRequiredValidator(
                    FORM_MESSAGES.requiredField,
                  ),
                }}
                defaultValue={isMulti ? [] : null}
              >
                {(field: AnyFieldApi) => {
                  const selectedOptions: SelectOption[] =
                    (field.state.value as SelectOption[]) || [];

                  const findUnselected = buildUnselectedFilter(selectedOptions);
                  const filterOptions = (opts: SelectOption[]) =>
                    opts.filter(findUnselected);

                  const rawValue = field.state.value;
                  const singleValue = Array.isArray(rawValue)
                    ? rawValue[0]
                    : rawValue;
                  const processedValue = isMulti
                    ? selectedOptions
                    : (singleValue ?? null);

                  const errors = buildFieldErrors(
                    dropDownName,
                    field.state.meta.errors,
                  );

                  const clearErrors = () => clearFieldErrors(field);

                  return (
                    <FormControl
                      error={field.state.meta.errors.length > 0}
                      key={dropDownName}
                      sx={{ mb: 4 }}
                      id={`${dropDownName}-${index}`}
                    >
                      <MultiSelect
                        isMultiple={isMulti}
                        id={dropDownName}
                        label={dropDownName}
                        isLabelMultiple={true}
                        errors={errors}
                        clearErrors={clearErrors}
                        options={getDropDownOptions(options)}
                        getOptionLabel={(option: SelectOption) =>
                          option?.label ?? ''
                        }
                        data-tracking={`documentAssociation-${index + 1}--${dropDownName}`}
                        data-testid={`documentAssociation-${index + 1}--${dropDownName}`}
                        disableClearable
                        filterOptions={filterOptions}
                        isOptionEqualToValue={(
                          option: SelectOption,
                          value: SelectOption | SelectOption[],
                        ) => {
                          if (isMulti) {
                            const singleValue = value as SelectOption;
                            return (
                              option?.value?.associationId ===
                                singleValue?.value?.associationId &&
                              option?.value?.associationLdId ===
                                singleValue?.value?.associationLdId
                            );
                          }
                          return checkSingleOptionEquality(option, value);
                        }}
                        onChange={(
                          _event: unknown,
                          value: SelectOption | SelectOption[] | null,
                        ) => {
                          if (isMulti) {
                            const multiValue = value as SelectOption[] | null;
                            if (!multiValue || multiValue.length === 0) {
                              handleChange(isMulti, dropDownName, [], null);
                              return;
                            }
                            const currentSelection = multiValue.at(-1);
                            if (!currentSelection?.label) return;
                            const newEntry = {
                              label: currentSelection.label,
                              value: currentSelection.value,
                            };
                            handleChange(
                              isMulti,
                              dropDownName,
                              [...multiValue.slice(0, -1), newEntry],
                              currentSelection,
                            );
                          } else {
                            const singleValue = value as SelectOption | null;
                            if (!singleValue?.label) {
                              handleChange(isMulti, dropDownName, null);
                              return;
                            }
                            handleChange(isMulti, dropDownName, [
                              {
                                label: singleValue.label,
                                value: singleValue.value,
                              },
                            ]);
                          }
                        }}
                        value={processedValue}
                      />
                    </FormControl>
                  );
                }}
              </form.Field>
            );
          },
        )}
      </>
    );
  },
});

export default ClassifyDynamicFormContent;
